const STORAGE_PREFIX = 'ay-tms.dashboard.pinned-columns.v1:';
const IMMUTABLE_COLUMN = 'container_no';

function columnKeys(columns) {
  return new Set((Array.isArray(columns) ? columns : [])
    .map(({ key } = {}) => key)
    .filter((key) => typeof key === 'string' && key));
}

export function normalizeDashboardPinnedColumns(value, columns) {
  const allowed = columnKeys(columns);
  const unique = new Set();
  for (const key of Array.isArray(value) ? value : []) {
    if (key === IMMUTABLE_COLUMN || !allowed.has(key)) continue;
    unique.add(key);
  }
  return [...unique];
}

export function orderDashboardColumns(columns, pinnedKeys) {
  const source = Array.isArray(columns) ? columns : [];
  const byKey = new Map(source.map((column) => [column.key, column]));
  const pins = normalizeDashboardPinnedColumns(pinnedKeys, source);
  const pinned = pins.map((key) => byKey.get(key)).filter(Boolean);
  const pinnedSet = new Set([IMMUTABLE_COLUMN, ...pins]);
  const container = byKey.get(IMMUTABLE_COLUMN);
  return [
    ...(container ? [container] : []),
    ...pinned,
    ...source.filter(({ key }) => !pinnedSet.has(key)),
  ];
}

/**
 * Where each pinned column sits, measured in the widths it is actually drawn at.
 *
 * widthOf exists because the drawn width is no longer a property of the column:
 * a date column is 24px narrower in Chinese than in English (2026-08-11). Read
 * preferredWidth here while the cell is drawn at the Chinese width and every
 * pinned column after the first lands 12-28px off, overlapping its neighbour.
 */
export function dashboardPinnedColumnOffsets(
  columns,
  pinnedKeys,
  widthOf = (column) => column?.preferredWidth,
) {
  const source = Array.isArray(columns) ? columns : [];
  const byKey = new Map(source.map((column) => [column.key, column]));
  const pinned = [IMMUTABLE_COLUMN, ...normalizeDashboardPinnedColumns(pinnedKeys, source)]
    .filter((key) => byKey.has(key));
  const offsets = new Map();
  let left = 0;
  for (const key of pinned) {
    offsets.set(key, left);
    const width = Number(widthOf(byKey.get(key)));
    left += Number.isFinite(width) && width > 0 ? width : 0;
  }
  return offsets;
}

export function createDashboardColumnPinStore({ storage, userKey, columns }) {
  if (!storage || typeof storage.getItem !== 'function' || typeof storage.setItem !== 'function'
    || typeof userKey !== 'string' || !userKey.trim()) {
    throw new TypeError('Dashboard column pin store is invalid.');
  }
  const key = `${STORAGE_PREFIX}${userKey.trim()}`;
  const read = () => {
    try {
      return normalizeDashboardPinnedColumns(JSON.parse(storage.getItem(key) || '[]'), columns);
    } catch {
      return [];
    }
  };
  const write = (value) => {
    const safe = normalizeDashboardPinnedColumns(value, columns);
    storage.setItem(key, JSON.stringify(safe));
    return safe;
  };
  return Object.freeze({
    load: read,
    save: write,
    toggle(columnKey) {
      if (columnKey === IMMUTABLE_COLUMN) return read();
      const current = read();
      return write(current.includes(columnKey)
        ? current.filter((keyValue) => keyValue !== columnKey)
        : [...current, columnKey]);
    },
  });
}
